import { GRID_TREE_DATA_GROUPING_FIELD, GridApiPro, GridColDef } from "@mui/x-data-grid-pro";
import { MutableRefObject } from "react";
import {
  AUDIT_FIELDS,
  rowAdBlueLiters,
  rowConsumption,
  rowDate,
  rowDistance,
  rowDriverIds,
  rowDurationMs,
  rowDutyRosters,
  rowFirstStart,
  rowLastStop,
  rowOdometerEndKm,
  rowOdometerStartKm,
  rowReasonText,
  rowRefuelLiters,
  rowStartTrip,
  rowStopTrip,
} from "../components/grid/rowValues";
import { defaultHiddenFields } from "../components/grid/useAuditColumns";
import { DriverLabel } from "../hooks/useDriverLabels";
import { AuditRow } from "../models/AuditRow";
import { AuditTrip } from "../models/AuditTrip";
import { AuditView } from "../models/AuditView";
import { buildAuditTable, buildSummaryTable, SummaryExportLabels, visibleExportColumns } from "./buildAuditExport";
import { formatConsumption, formatDuration } from "./format";
import { PeriodSummary } from "./periodSummary";
import { formatClockInZone, formatDayInZone } from "./timeZone";
import { FuelType } from "@/constants/FuelTypeGroups";
import { Report } from "@/utilities/export/report";
import { WorkbookCell } from "@/utilities/xlsx";

export type SummaryLabels = SummaryExportLabels & { sheet: string };

/** What every audit report states about the run, whatever its table. */
interface ReportHeader {
  /** Already translated; the main sheet and the file are named after it. */
  title: string;
  /** Picked calendar days. */
  fromDay: Date;
  toDay: Date;
  /** Already formatted names of the selected groups, vehicles and reasons. */
  filter: string;
  /** Labelled facts shown under the standard preamble. */
  extra: [label: string, value: WorkbookCell][];
}

interface Args extends ReportHeader {
  apiRef: MutableRefObject<GridApiPro>;
  /** Column definitions, used when the grid is not mounted (mobile cards). */
  columns: GridColDef<AuditRow>[];
  rows: AuditRow[];
  view: AuditView;
  /** Zone for dates and clock times in the sheet. */
  timeZone: string;
  language: string;
  distanceDecimals: number;
  levelColumn: { header: string; day: string; trip: string };
  /** Header of the vehicle column when the grid is not mounted. */
  deviceHeader: string;
  /** Translated energy product per drive type. */
  fuelTypeLabels: Record<FuelType, string>;
  driverLabel: DriverLabel;
  getStartAddress: (trip: AuditTrip) => string;
  getStopAddress: (trip: AuditTrip) => string;
  /** Per-vehicle totals of the period; written to a second sheet. */
  summary: PeriodSummary;
  summaryLabels: SummaryLabels;
  /** Whether the period allows the consumption split (at least one full calendar month). */
  includeSplit: boolean;
  /** Appended to the fuel column's header in the digital view, where electric vehicles report kWh. */
  refuelUnitHint: string;
}

/** Fraction digits of the numeric columns; the drivers' odometer entries are whole kilometres. */
function numericDigits(view: AuditView, distanceDecimals: number): Record<string, number> {
  const odometer = view === "analog" ? 0 : 1;
  return {
    [AUDIT_FIELDS.distance]: view === "analog" ? 0 : distanceDecimals,
    [AUDIT_FIELDS.odometerStart]: odometer,
    [AUDIT_FIELDS.odometerEnd]: odometer,
    [AUDIT_FIELDS.refuel]: 1,
    [AUDIT_FIELDS.adBlue]: 1,
  };
}

/**
 * The audit report: the visible grid columns with day rows and trip rows, and the period summary
 * as a second sheet. Standard preamble and logo come from the shared export pipeline.
 */
export function buildAuditReport({
  apiRef,
  columns: columnDefs,
  rows,
  view,
  title,
  fromDay,
  toDay,
  filter,
  extra,
  timeZone,
  language,
  distanceDecimals,
  levelColumn,
  deviceHeader,
  fuelTypeLabels,
  driverLabel,
  getStartAddress,
  getStopAddress,
  summary,
  summaryLabels,
  includeSplit,
  refuelUnitHint,
}: Args): Report {
  // On phones the grid is replaced by cards, so there is no grid state to read; fall back to
  // the column definitions with the default visibility.
  const gridMounted = !!apiRef.current?.state?.columns;
  const sourceColumns: { field: string; headerName?: string; disableExport?: boolean }[] = gridMounted
    ? apiRef.current.getAllColumns()
    : [{ field: GRID_TREE_DATA_GROUPING_FIELD, headerName: deviceHeader }, ...columnDefs];
  const digits = numericDigits(view, distanceDecimals);
  const allColumns = sourceColumns.map(column => ({
    field: column.field,
    header:
      column.field === AUDIT_FIELDS.refuel && view === "digital"
        ? `${column.headerName ?? column.field} (${refuelUnitHint})`
        : (column.headerName ?? column.field),
    disableExport: column.disableExport,
    fractionDigits: digits[column.field],
  }));
  const visibilityModel = gridMounted
    ? apiRef.current.state.columns.columnVisibilityModel
    : Object.fromEntries(defaultHiddenFields(view).map(field => [field, false]));
  const orderedFields = gridMounted
    ? apiRef.current.state.columns.orderedFields
    : sourceColumns.map(column => column.field);
  const columns = visibleExportColumns(allColumns, visibilityModel, orderedFields);

  const clock = (date: Date) => formatClockInZone(date, timeZone);
  const getCell = (row: AuditRow, field: string): WorkbookCell => {
    switch (field) {
      case GRID_TREE_DATA_GROUPING_FIELD:
        return row.device.name;
      case AUDIT_FIELDS.licensePlate:
        return row.device.licensePlate;
      case AUDIT_FIELDS.vin:
        return row.device.vin;
      case AUDIT_FIELDS.date:
        return formatDayInZone(rowDate(row), timeZone);
      case AUDIT_FIELDS.driver:
        return rowDriverIds(row).map(driverLabel).join(", ");
      case AUDIT_FIELDS.distance: {
        const distance = rowDistance(row);
        return distance == null ? null : Number(distance.toFixed(digits[AUDIT_FIELDS.distance]));
      }
      case AUDIT_FIELDS.duration: {
        const duration = rowDurationMs(row);
        return duration == null ? "" : formatDuration(duration);
      }
      case AUDIT_FIELDS.fuelType:
        return row.device.fuelType ? fuelTypeLabels[row.device.fuelType] : "";
      case AUDIT_FIELDS.adBlue:
        return rowAdBlueLiters(row) ?? null;
      case AUDIT_FIELDS.firstStart:
        return clock(rowFirstStart(row));
      case AUDIT_FIELDS.startAddress:
        return getStartAddress(rowStartTrip(row));
      case AUDIT_FIELDS.lastStop: {
        const stop = rowLastStop(row);
        return stop ? clock(stop) : "";
      }
      case AUDIT_FIELDS.stopAddress:
        return getStopAddress(rowStopTrip(row));
      case AUDIT_FIELDS.consumption: {
        const { value, fuelType } = rowConsumption(row);
        return formatConsumption(value, fuelType, language);
      }
      case AUDIT_FIELDS.reason:
        return rowReasonText(row);
      case AUDIT_FIELDS.dutyRoster:
        return rowDutyRosters(row).join(", ");
      case AUDIT_FIELDS.odometerStart:
        return rowOdometerStartKm(row) ?? null;
      case AUDIT_FIELDS.odometerEnd:
        return rowOdometerEndKm(row) ?? null;
      case AUDIT_FIELDS.refuel:
        return rowRefuelLiters(row) ?? null;
      default:
        return null;
    }
  };

  const { sheet: summaryName, ...labels } = summaryLabels;
  return {
    title,
    period: { from: fromDay, to: toDay },
    filter,
    extra,
    table: buildAuditTable(columns, rows, getCell, levelColumn),
    extraSheets: [{ name: summaryName, table: buildSummaryTable(summary, labels, includeSplit) }],
  };
}

interface SummaryArgs extends ReportHeader {
  summary: PeriodSummary;
  summaryLabels: SummaryLabels;
  includeSplit: boolean;
}

/** The period summary alone, as the main sheet of its own report. */
export function buildSummaryReport({
  title,
  fromDay,
  toDay,
  filter,
  extra,
  summary,
  summaryLabels,
  includeSplit,
}: SummaryArgs): Report {
  return {
    title,
    period: { from: fromDay, to: toDay },
    filter,
    extra,
    table: buildSummaryTable(summary, summaryLabels, includeSplit),
  };
}
